import React, { FC, useMemo } from "react";
import {
	Box,
	Checkbox,
	Flex,
	Text,
	Button,
	ModalBody,
	ModalFooter,
	ModalHeader,
	Tag,
	TagLabel,
	Table,
	Td,
	Tr,
	Thead,
} from "@chakra-ui/react";
import { TableContainer, Th } from "@/components/ui/Table";
import { default as IconPinClass } from "@/assets/IconPinClass.svg";
import Tooltip from "rc-tooltip";
import useStore from "@/stores/index";
import { getClassroomStore } from "@/stores/classroomStore";
import { ClassroomOverviewDTO } from "@/stores/classroomStore_I";
import { getProviderDisplayText } from "@/js/utils/providerUtils";
import { ClassroomSourceType } from "@/types/classrooms";
import { ClassroomType } from "@/types/classrooms";
import { colours } from "@/utils/themeUtils";
import { Notice } from "@/components/Notice";
import { ClasswizeModal } from "@/components/ui/Modal";
import { Tbody } from "@familyzone/component-library";

type props = {
	open: boolean;
	onClose: () => void;
	onSubmit: () => void;
	selected: ClassroomOverviewDTO[];
	setSelected: (selected: ClassroomOverviewDTO[]) => void;
};

const headerText = "Combine synced classes into one class";
const tagText = "Step 1/2";
const bodyText = "Select the classes you want to combine. (Maximum 25 classes and up to 100 students)";
const descText = "After the classes are combined, the classes selected below will not appear on your dashboard";
const buttonText = "Next";
const tooltipText = "End the session for this class to combine.";
const notificationText = "The combined student count should not be more than 100";
const maximumNumClassesAllowedToBeCombined = 25;

const columns = [
	{
		headerText: "Class name",
		columnName: "className",
	},
	{
		headerText: "Provider",
		columnName: "provider",
	},
	{
		headerText: "Students",
		columnName: "students",
	},
];

export const FirstStepModal: FC<props> = ({ open, onClose, onSubmit, selected, setSelected }) => {
	function getClasses(): ClassroomOverviewDTO[] {
		return getClassroomStore(useStore.getState()).getClassrooms() as ClassroomOverviewDTO[];
	}

	const data: ClassroomOverviewDTO[] = useMemo(
		() =>
			getClasses()
				.filter((item: ClassroomOverviewDTO) => item.canCombine)
				.sort((item: ClassroomOverviewDTO) => (item.personalisation?.pinned ? -1 : 1)),

		// Although this will fire when the modal both opens and closes (which is redundant), this value isn't making an api call so it won't cause extra downstream load,
		// and this will ensure that the list of available classes to combine is updated if the user wants to combine multiple sets of classes without a refresh
		// eslint-disable-next-line
		[open]
	);

	const onSelect = (item: ClassroomOverviewDTO) => {
		if (isItemDisabled(item)) return;
		// eslint-disable-next-line @typescript-eslint/no-unused-expressions
		selected.includes(item) ? setSelected(selected.filter((i) => i !== item)) : setSelected([...selected, item]);
	};

	const isSelected = (item: ClassroomOverviewDTO) => selected.includes(item);

	const exceedsStudentLimit = useMemo(() => selected.reduce((acc, item) => acc + item.memberCount, 0) > 100, [selected]);

	function isClassActive(classId: string): boolean {
		return getClassroomStore(useStore.getState()).isActiveV2(classId) as boolean;
	}

	/* Item is disabled if it is
	 * 1. active
	 * 2. not selected and selected length is maximumNumClassesAllowedToBeCombined (full)
	 * 3. not selected and exceeds student limit
	 */
	function isItemDisabled(item: ClassroomOverviewDTO): boolean {
		return (
			isClassActive(item.id) || (!isSelected(item) && (selected.length >= maximumNumClassesAllowedToBeCombined || exceedsStudentLimit))
		);
	}

	const tableDataMap = (item: ClassroomOverviewDTO, index: number) => {
		// Same logic as in ClassroomTileV2
		const displayName =
			item.personalisation?.displayName ||
			((item.type.toLowerCase() as ClassroomType) === ClassroomType.Group ? item.description : item.name);
		const classProvider = getProviderDisplayText(
			item.sourceType.toLowerCase() as ClassroomSourceType,
			item.type.toLowerCase() as ClassroomType
		);

		const nameColumn = (
			<Checkbox
				name="alert"
				isChecked={isSelected(item)}
				onChange={() => onSelect(item)}
				isDisabled={isItemDisabled(item)}
				data-testid={`checkbox-${index}`}
			>
				<Flex alignItems="center" ml={2}>
					{item.personalisation?.pinned && (
						<div className="Pin_button_combine_class" data-testid="pinned-icon">
							<div className="Pin_active">
								<IconPinClass />
							</div>
						</div>
					)}
					{displayName}
				</Flex>
			</Checkbox>
		);

		return (
			<Tr key={index}>
				<Td>
					{isClassActive(item.id) ? (
						<Tooltip
							placement="topLeft"
							trigger={["hover"]}
							destroyTooltipOnHide={true}
							overlayClassName="Tooltip"
							overlay={<span>{tooltipText}</span>}
						>
							{nameColumn}
						</Tooltip>
					) : (
						nameColumn
					)}
				</Td>
				<Td>
					<Text fontSize="md">{classProvider}</Text>
				</Td>
				<Td>
					<Text fontSize="md">{item.memberCount}</Text>
				</Td>
			</Tr>
		);
	};

	return (
		<ClasswizeModal isOpen={open} onClose={onClose} title={headerText} size="5xl">
			<ModalHeader>
				<Flex alignItems="center" gap={6}>
					<Flex>
						<Tag backgroundColor="#E0FFFD" borderRadius="full" p={2} px={4}>
							<TagLabel color="#051839" fontFamily="Roboto" fontSize="14px">
								{tagText}
							</TagLabel>
						</Tag>
					</Flex>
					<Flex direction="column">
						<Flex>
							<Box color="#051839" fontFamily="Roboto" fontWeight={500} fontSize="16px" lineHeight="20px">
								{bodyText}
							</Box>
						</Flex>
						<Flex>
							<Box color={colours.navy[800]} fontFamily="Roboto" fontSize="14px" fontWeight={400} lineHeight="20px">
								{descText}
							</Box>
						</Flex>
					</Flex>
				</Flex>
			</ModalHeader>

			<ModalBody>
				<TableContainer>
					<Table variant="qoriaStriped">
						<Thead>
							<Tr>
								{columns.map(({ headerText, columnName }) => (
									<Th key={columnName}>{headerText}</Th>
								))}
							</Tr>
						</Thead>
						<Tbody>{data.map(tableDataMap)}</Tbody>
					</Table>
				</TableContainer>
			</ModalBody>

			<ModalFooter>
				<Flex>
					{exceedsStudentLimit && (
						<Flex>
							<Notice status="error" children={notificationText} />
						</Flex>
					)}
					<Flex grow={1} />
					<Flex>
						<Button
							isDisabled={selected.length < 2 || exceedsStudentLimit}
							onClick={onSubmit}
							borderRadius="md"
							data-testid="modal-next-btn"
						>
							{buttonText}
						</Button>
					</Flex>
				</Flex>
			</ModalFooter>
		</ClasswizeModal>
	);
};
